import TaskItemsGroup from './taskItemsGroup';
import { DealProvider, useDeal } from './context';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowDownCircleIcon
} from '@heroicons/react/24/outline';
import { sortBy } from 'lodash';
import { Skeleton } from '../utils/skeleton';

function EmptyGroup({ mode }: { mode: 'viewer' | 'editor' }) {
  const phrase = mode === 'viewer' ? 'Aucun document' : 'Aucune démarche';

  return (
    <div className="min-h-[300px] border-2 border-dashed rounded-lg flex items-center justify-center">
      <div className="text-sm font-hmedium tracking-tight">{phrase}</div>
    </div>
  );
}

// Every document of the deal the client can see, in one archive, closing both
// sections rather than repeating itself above each one.
//
// data-turbo="false" so the browser handles the response as a download rather
// than Turbo trying to render a zip as a page — the same reason the per-row
// download link sets it.
function DownloadAll({ dealId }: { dealId: string }) {
  return (
    <div className="flex justify-center py-12">
      <a
        data-turbo="false"
        href={`/guest/deals/${dealId}/download`}
        className="border border-blue-600 bg-white rounded-full text-sm font-hmedium tracking-tight flex items-center text-blue-600 py-3 px-7 justify-center hover:bg-blue-600 hover:text-white space-x-2"
      >
        <ArrowDownCircleIcon className="w-5 h-5" />
        <span>Télécharger tous les documents</span>
      </a>
    </div>
  );
}

function TaskItemsList() {
  const { dealId, taskItemsGroups, closedGroupIds, toggleAllGroups, initialLoading, currentUser } =
    useDeal();
  const sortedGroups = sortBy(taskItemsGroups, 'position');
  const sortingTrigger = JSON.stringify(
    sortedGroups.map(g => ({ id: g.id, position: g.position }))
  );

  if (initialLoading) {
    return (
      <div className="mt-6">
        <Skeleton />
      </div>
    );
  }

  const sortedGroupsWithEditors = sortedGroups
    .map(g => ({
      ...g,
      task_items: g.task_items.filter(t => t.editors.map(e => e.email).includes(currentUser.email))
    }))
    .filter(g => g.task_items.length > 0);

  const sortedGroupsWithViewers = sortedGroups
    .map(g => ({
      ...g,
      task_items: g.task_items.filter(t => t.viewers.map(e => e.email).includes(currentUser.email))
    }))
    .filter(g => g.task_items.length > 0);

  // Offered only when the archive would contain something. A démarche still
  // waiting on the client has no file yet, so both sections can be full of rows
  // and still have nothing to download.
  const countFiles = groups =>
    groups.reduce(
      (total, group) =>
        total + group.task_items.reduce((sum, item) => sum + (item.files_count || 0), 0),
      0
    );

  const downloadableFilesCount =
    countFiles(sortedGroupsWithEditors) + countFiles(sortedGroupsWithViewers);

  return (
    <div className="mt-6 z-[11]">
      <div className="flex flex-col space-y-6">
        <>
          <div>
            <h3 className="!font-hbold text-2xl px-6 tracking-tight">Démarches à compléter</h3>
            <p className="mb-1 text-slate-600 px-6">
              Retrouvez toutes les démarches qui vous ont été attribuées.
            </p>
          </div>
          {sortedGroupsWithEditors.map(group => {
            return <TaskItemsGroup key={group.id} groupId={group.id} mode="editor" />;
          })}
          {sortedGroupsWithEditors.length <= 0 && <EmptyGroup mode="editor" />}
        </>

        <>
          <div>
            <h3 className="!font-hbold text-2xl px-6 tracking-tight">Démarches partagées</h3>
            <p className="mb-1 text-slate-600 px-6">
              Retrouvez toutes les démarches qui vous ont été partagées pour votre information.
            </p>
          </div>
          {sortedGroupsWithViewers.map(group => {
            return <TaskItemsGroup key={group.id} groupId={group.id} mode="viewer" />;
          })}
          {sortedGroupsWithViewers.length <= 0 && <EmptyGroup mode="viewer" />}
        </>

        {downloadableFilesCount > 0 && <DownloadAll dealId={dealId} />}
      </div>
    </div>
  );
}

export default function Index({ dealId }) {
  return (
    <DealProvider dealId={dealId}>
      <TaskItemsList />
    </DealProvider>
  );
}
